
import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Centred window as drawn on the help / onboarding boards: #1B1C31 veil at 42 %
 * with a 2 px blur, 22 px corners, header with an icon orb, title, subtitle and
 * a 34 px close button. Esc, veil and × close it unless `locked` (request in flight).
 */
export function Window({
  open,
  onClose,
  width = 560,
  icon,
  art,
  title,
  subtitle,
  locked,
  label,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  width?: number;
  icon?: ReactNode;
  /** Bare illustration (e.g. a 56 px character) in place of the icon orb. */
  art?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  locked?: boolean;
  /** aria-label when there is no visible title. */
  label?: string;
  children: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={label ?? (typeof title === "string" ? title : undefined)}
      onCancel={(e) => {
        e.preventDefault();
        if (!locked) onClose();
      }}
      onClick={(e) => {
        if (!locked && e.target === e.currentTarget) onClose();
      }}
      style={{ "--w": `${width}px` } as CSSProperties}
      className={cn(
        "m-auto max-h-[min(852px,88vh)] w-[min(var(--w),calc(100vw-32px))] overflow-hidden rounded-[22px] max-sm:mb-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-[24px] bg-white p-0 text-ink shadow-[0_40px_90px_-30px_rgba(27,28,49,.55)]",
        "backdrop:bg-ink/42 backdrop:backdrop-blur-[2px] open:animate-[pop_.18s_ease-out]",
        className,
      )}
    >
      {open && (
        <div className="flex max-h-[min(852px,88vh)] flex-col">
          {(title || !locked) && (
            <div className="flex items-start gap-3.5 px-6 pt-[22px]">
              {art}
              {icon && <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-500">{icon}</span>}
              <div className="min-w-0 flex-1">
                {title && <h2 className="text-lg font-extrabold tracking-[-0.01em] text-ink">{title}</h2>}
                {subtitle && <p className="mt-1 text-[12.5px] leading-[1.55] text-muted">{subtitle}</p>}
              </div>
              <button
                type="button"
                onClick={() => !locked && onClose()}
                disabled={locked}
                aria-label="Fermer"
                className="inline-flex size-[34px] shrink-0 items-center justify-center rounded-[10px] border border-line text-ink-2 hover:border-line-strong disabled:opacity-50"
              >
                <X className="size-4" strokeWidth={1.9} />
              </button>
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        </div>
      )}
    </dialog>
  );
}
